import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useLanguageStore } from '../../store/languageStore'
import { useTabStore } from '../../store/tabStore'
import { menuItems, MenuItem } from '../../data/menuItems'
import { 
  FiGlobe, FiChevronDown, FiChevronRight, FiMenu, FiX, FiSearch, 
  FiFileText, FiEdit, FiLayout, FiClipboard, FiFile, FiCheckSquare, 
  FiList, FiStar, FiMove, FiSettings, FiRefreshCw, FiZap, FiEye, FiEyeOff, FiDroplet, FiTrash2,
  FiHome, FiBriefcase, FiLayers, FiPackage, FiUsers, FiShoppingCart, FiBarChart, FiBox,
  FiDollarSign, FiShield, FiCreditCard, FiPrinter, FiKey, FiShoppingBag, FiAward, FiCalendar, FiPlus,
  FiFolderPlus, FiTarget, FiCommand, FiFolder, FiMaximize2
} from 'react-icons/fi'
import { useState, useMemo, useEffect, useCallback, useRef, Fragment } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuthStore } from '../../store/authStore'
import { useLicenseStatus } from '../../hooks/useLicenseStatus'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { customFormService } from '../../services/customFormService'
import { dynamicReportService } from '../../api/dynamicReports'
import { useMenuStore } from '../../store/menuStore'
import { useModuleStore } from '../../store/moduleStore'
import { useUiLayoutStore } from '../../store/uiLayoutStore'
import { filterMenuItems } from '../../utils/moduleHelpers'
import { getAppVersion } from '../../utils/version'
import LoadingSpinner from '../common/LoadingSpinner'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  TouchSensor,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Popover, Transition, Portal } from '@headlessui/react'

interface StudioMenuScreen {
  id: string
  name: string
  nameEn?: string
}

interface SidebarProps {
  currentPath: string
  isOpen: boolean
  onToggle: () => void
}

const EMPTY_DYNAMIC_REPORTS: { path: string; name: string; menuName?: string }[] = []

const iconOptions = {
  FiHome, FiFileText, FiEdit, FiLayout, FiClipboard, FiFile, FiCheckSquare, 
  FiList, FiBriefcase, FiLayers, FiPackage, FiUsers, FiShoppingCart, FiBarChart, FiBox,
  FiDollarSign, FiShield, FiCreditCard, FiPrinter, FiKey, FiShoppingBag, FiAward, FiCalendar, FiStar, FiZap
}

// Sortable Wrapper for Menu Items
function SortableItem({ 
  id, 
  children, 
  isCustomizing, 
  isRTL, 
  isHidden, 
  onToggleVisibility,
  customColors,
  onSetColors,
  onResetColors,
  isFirst,
  onSetIcon,
  onSetLabel,
  onTogglePinned,
  isPinned,
  currentLabel,
  userGroups,
  onToggleInGroup
}: { 
  id: string; 
  children: React.ReactNode; 
  isCustomizing: boolean; 
  isRTL: boolean;
  isHidden?: boolean;
  onToggleVisibility?: () => void;
  customColors?: { textColor?: string; backgroundColor?: string };
  onSetColors?: (colors: { textColor?: string; backgroundColor?: string }) => void;
  onResetColors?: () => void;
  isFirst?: boolean;
  onSetIcon?: (iconName: string) => void;
  onSetLabel?: (label: string) => void;
  onTogglePinned?: () => void;
  isPinned?: boolean;
  currentLabel?: string;
  userGroups?: any[];
  onToggleInGroup?: (groupId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const [isEditingLabel, setIsEditingLabel] = useState(false)
  const [tempLabel, setTempLabel] = useState(currentLabel || '')
  const [iconPopoverPos, setIconPopoverPos] = useState<{ top: number; left?: number; right?: number } | null>(null)
  const [colorPopoverPos, setColorPopoverPos] = useState<{ top: number; left?: number; right?: number } | null>(null)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 100 : 1,
    scale: isDragging ? '1.02' : '1',
  }

  const presetColors = [
    { bg: 'rgba(59, 130, 246, 0.2)', text: '#60a5fa' }, // Blue
    { bg: 'rgba(16, 185, 129, 0.2)', text: '#34d399' }, // Green
    { bg: 'rgba(245, 158, 11, 0.2)', text: '#fbbf24' }, // Amber
    { bg: 'rgba(239, 68, 68, 0.2)', text: '#f87171' },  // Red
    { bg: 'rgba(139, 92, 246, 0.2)', text: '#a78bfa' }, // Purple
    { bg: 'rgba(236, 72, 153, 0.2)', text: '#f472b6' }, // Pink
  ]
  const popoverAlignClass = isRTL ? 'right-0' : 'left-0'
  const resolvePopoverPos = (target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    const top = rect.bottom + 8
    if (isRTL) {
      return { top, right: Math.max(12, window.innerWidth - rect.right) }
    }
    return { top, left: Math.max(12, rect.left) }
  }

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`relative group ${isCustomizing ? 'animate-jiggle' : ''} ${isDragging ? 'shadow-2xl shadow-primary-500/20' : ''}`}
    >
      {isCustomizing && (
        <div 
          className={`absolute ${isRTL ? 'left-0' : 'right-0'} -top-8 flex items-center gap-1 z-50 bg-[#1e293b] border border-white/20 rounded-xl px-1.5 py-1 shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur-md animate-fade-in opacity-0 group-hover:opacity-100 transition-opacity duration-200`}
          onClick={e => e.stopPropagation()}
        >
          {onTogglePinned && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onTogglePinned()
              }}
              className={`p-2 transition-all rounded-lg hover:bg-white/5 ${isPinned ? 'text-yellow-400' : 'text-white/70 hover:text-white'}`}
              title={isPinned ? (isRTL ? 'إلغاء التثبيت' : 'Unpin') : (isRTL ? 'تثبيت في الوصول السريع' : 'Pin to Quick Access')}
            >
              <FiStar size={16} fill={isPinned ? 'currentColor' : 'none'} />
            </button>
          )}

          <div 
            {...attributes} 
            {...listeners}
            className="p-2 cursor-grab active:cursor-grabbing text-white/70 hover:text-primary-400 transition-all hover:bg-white/5 rounded-lg"
            title={isRTL ? 'سحب للترتيب' : 'Drag to reorder'}
          >
            <FiMove size={16} />
          </div>
          
          {onToggleVisibility && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onToggleVisibility()
              }}
              className={`p-2 transition-all rounded-lg hover:bg-white/5 ${isHidden ? 'text-red-400' : 'text-white/70 hover:text-white'}`}
              title={isHidden ? (isRTL ? 'إظهار' : 'Show') : (isRTL ? 'إخفاء' : 'Hide')}
            >
              {isHidden ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          )}

          {onToggleInGroup && userGroups && userGroups.length > 0 && (
            <Popover className="relative flex items-center">
              <Popover.Button
                className="p-2 text-white/70 hover:text-primary-400 transition-all hover:bg-white/5 rounded-lg outline-none"
                title={isRTL ? 'إضافة لمجموعة' : 'Add to Group'}
              >
                <FiFolder size={16} />
              </Popover.Button>
              <Popover.Panel
                transition
                className={`z-[10000] absolute ${popoverAlignClass} top-full mt-2 transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0`}
              >
                <div 
                  className="bg-[#1a1f2e] border border-white/20 rounded-2xl p-3 shadow-[0_20px_50px_rgba(0,0,0,0.8)] min-w-[200px] animate-fade-in backdrop-blur-xl"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2 px-2">
                    {isRTL ? 'اختر مجموعة' : 'Select Group'}
                  </div>
                  <div className="space-y-1">
                    {userGroups.map(g => (
                      <button
                        key={g.id}
                        onClick={() => {
                          onToggleInGroup(g.id)
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors flex items-center gap-2 ${g.items.includes(id) ? 'bg-primary-500/20 text-primary-400' : 'hover:bg-white/5 text-white/60'}`}
                      >
                        <FiFolder size={12} />
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Popover>
          )}

          {onSetIcon && (
            <Popover className="relative flex items-center">
              <Popover.Button
                className="p-2 text-white/70 hover:text-primary-400 transition-all hover:bg-white/5 rounded-lg outline-none"
                title={isRTL ? 'تغيير الأيقونة' : 'Change Icon'}
                onClick={(e) => {
                  setIconPopoverPos(resolvePopoverPos(e.currentTarget))
                }}
              >
                <FiLayers size={16} />
              </Popover.Button>
              <Portal>
                <Popover.Panel
                  transition
                  style={iconPopoverPos || undefined}
                  className="z-[10000] fixed transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                >
                  <div
                    className="bg-[#1a1f2e] border border-white/20 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.8)] min-w-[240px] animate-fade-in max-h-64 overflow-y-auto custom-scrollbar backdrop-blur-xl"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center mb-4 pb-2 border-b border-white/5">
                      <span className="text-[11px] font-bold text-primary-400 uppercase tracking-widest">
                        {isRTL ? 'اختر أيقونة' : 'Select Icon'}
                      </span>
                    </div>
                    <div className="grid grid-cols-5 gap-3">
                      {Object.entries(iconOptions).map(([name, Icon]) => (
                        <button
                          key={name}
                          onClick={() => {
                            onSetIcon(name)
                          }}
                          className="p-2.5 bg-white/5 hover:bg-primary-500/20 rounded-xl text-white hover:text-primary-400 transition-all flex items-center justify-center border border-white/5 hover:border-primary-500/30 active:scale-90"
                        >
                          <Icon size={18} />
                        </button>
                      ))}
                    </div>
                  </div>
                </Popover.Panel>
              </Portal>
            </Popover>
          )}

          {onSetLabel && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsEditingLabel(true)
              }}
              className="p-2 text-white/70 hover:text-primary-400 transition-all hover:bg-white/5 rounded-lg"
              title={isRTL ? 'تعديل الاسم' : 'Edit Name'}
            >
              <FiEdit size={16} />
            </button>
          )}

          {onSetColors && (
            <Popover className="relative flex items-center">
              <Popover.Button
                className={`p-2 transition-all rounded-lg hover:bg-white/5 ${customColors?.backgroundColor ? 'text-primary-400' : 'text-white/70 hover:text-white'} outline-none`}
                title={isRTL ? 'تغيير الألوان' : 'Change Colors'}
                onClick={(e) => {
                  setColorPopoverPos(resolvePopoverPos(e.currentTarget))
                }}
              >
                <FiDroplet size={16} />
              </Popover.Button>
              <Portal>
                <Popover.Panel
                  transition
                  style={colorPopoverPos || undefined}
                  className="z-[10000] fixed transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                >
                  <div
                    className="bg-[#1a1f2e] border border-white/20 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.8)] min-w-[200px] animate-fade-in backdrop-blur-xl"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center mb-4 pb-2 border-b border-white/5">
                      <span className="text-[11px] font-bold text-primary-400 uppercase tracking-widest">
                        {isRTL ? 'اختر لوناً' : 'Pick a color'}
                      </span>
                      <button
                        onClick={() => {
                          onResetColors?.()
                        }}
                        className="text-red-400 hover:text-red-300 p-1.5 hover:bg-red-500/10 rounded-lg transition-colors"
                        title={isRTL ? 'إعادة تعيين' : 'Reset'}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {presetColors.map((c, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            onSetColors({ backgroundColor: c.bg, textColor: c.text })
                          }}
                          className="w-full h-10 rounded-xl border-2 border-white/5 transition-all hover:scale-110 active:scale-95 shadow-lg relative overflow-hidden group/color"
                          style={{ backgroundColor: c.bg, borderColor: c.text + '40' }}
                        >
                          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover/color:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                </Popover.Panel>
              </Portal>
            </Popover>
          )}
        </div>
      )}

      {isEditingLabel && (
        <div 
          className="absolute inset-0 z-40 bg-[#1e293b]/95 backdrop-blur-sm flex items-center px-4 rounded-xl border border-primary-500/50 shadow-2xl animate-fade-in"
          onClick={e => e.stopPropagation()}
        >
          <input
            autoFocus
            type="text"
            value={tempLabel}
            onChange={e => setTempLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onSetLabel?.(tempLabel)
                setIsEditingLabel(false)
              }
              if (e.key === 'Escape') setIsEditingLabel(false)
            }}
            className="flex-1 bg-transparent text-white outline-none text-sm font-bold placeholder:text-white/20"
            placeholder={isRTL ? 'اكتب الاسم الجديد...' : 'Type new name...'}
          />
          <div className="flex gap-1">
            <button 
              onClick={() => {
                onSetLabel?.(tempLabel)
                setIsEditingLabel(false)
              }}
              className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
            >
              <FiCheckSquare size={18} />
            </button>
            <button 
              onClick={() => setIsEditingLabel(false)}
              className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>
      )}

      <div className={isHidden && isCustomizing ? 'opacity-40 grayscale-[0.5]' : ''}>
        {children}
      </div>
    </div>
  )
}

export default function Sidebar({ currentPath, isOpen, onToggle }: SidebarProps) {
  const [isHovered, setIsHovered] = useState(false)
  const isExpanded = isOpen || isHovered
  const { language, direction, toggleLanguage } = useLanguageStore()
  const { addTab } = useTabStore()
  const { toggleContentFullscreen } = useUiLayoutStore()
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const { hasPermission, isPermissionsLoading } = usePermissions()
  const { user } = useAuthStore()
  const { canAccess } = useLicenseStatus()
  const isRTL = direction === 'rtl'
  
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [studioScreens, setStudioScreens] = useState<StudioMenuScreen[]>([])
  const sidebarRef = useRef<HTMLDivElement>(null)

  const persistStudioScreens = (screens: StudioMenuScreen[]) => {
    try {
      localStorage.setItem('studio_custom_screens_v1', JSON.stringify(screens))
      setStudioScreens(screens)
      window.dispatchEvent(new Event('studio-screens-updated'))
    } catch {
      toast.error(isRTL ? 'تعذر حفظ تغييرات الشاشات' : 'Could not save screens changes')
    }
  }

  useEffect(() => {
    const loadStudioScreens = () => {
      try {
        const raw = localStorage.getItem('studio_custom_screens_v1')
        const parsed = raw ? JSON.parse(raw) : []
        if (Array.isArray(parsed)) {
          setStudioScreens(parsed)
        }
      } catch {
        setStudioScreens([])
      }
    }

    loadStudioScreens()
    window.addEventListener('storage', loadStudioScreens)
    window.addEventListener('studio-screens-updated', loadStudioScreens as EventListener)

    return () => {
      window.removeEventListener('storage', loadStudioScreens)
      window.removeEventListener('studio-screens-updated', loadStudioScreens as EventListener)
    }
  }, [])

  const isStudioScreenPath = (path?: string) => {
    return !!path && path.startsWith('/settings/studio?tab=designer&screenId=')
  }

  const extractScreenIdFromPath = (path?: string) => {
    if (!isStudioScreenPath(path) || !path) return ''
    const query = path.split('?')[1] || ''
    const params = new URLSearchParams(query)
    return params.get('screenId') || ''
  }

  const renameStudioScreenFromMenu = (path?: string) => {
    const screenId = extractScreenIdFromPath(path)
    if (!screenId) return
    const current = studioScreens.find(screen => screen.id === screenId)
    if (!current) return
    const nextName = window.prompt(
      isRTL ? 'اكتب الاسم الجديد للشاشة' : 'Enter new screen name',
      current.name || current.nameEn || ''
    )
    if (!nextName || !nextName.trim()) return
    const updated = studioScreens.map(screen =>
      screen.id === screenId ? { ...screen, name: nextName.trim(), nameEn: screen.nameEn || nextName.trim() } : screen
    )
    persistStudioScreens(updated)
    toast.success(isRTL ? 'تمت إعادة تسمية الشاشة' : 'Screen renamed')
  }

  const deleteStudioScreenFromMenu = (path?: string) => {
    const screenId = extractScreenIdFromPath(path)
    if (!screenId) return
    if (!window.confirm(isRTL ? 'هل تريد حذف هذه الشاشة؟' : 'Delete this screen?')) return
    const updated = studioScreens.filter(screen => screen.id !== screenId)
    persistStudioScreens(updated)
    toast.success(isRTL ? 'تم حذف الشاشة' : 'Screen deleted')
  }

  // Reset search and hover when expanded/collapsed
  useEffect(() => {
    if (!isExpanded) {
      setSearchTerm('')
    }
  }, [isExpanded])

  const handleCreateGroup = () => {
    const name = prompt(isRTL ? 'اسم المجموعة الجديدة:' : 'New Group Name:')
    if (name) {
      addUserGroup({
        id: `group-${Date.now()}`,
        label: name,
        icon: 'FiFolder',
        items: []
      })
    }
  }

  const { 
    sidebarOrder, setSidebarOrder, 
    pinnedItems, setPinnedItems, togglePinnedItem,
    hiddenItems, toggleHiddenItem,
    itemColors, setItemColors, resetItemColors,
    itemIcons, setItemIcon,
    itemLabels, setItemLabel,
    recentItems, addRecentItem,
    userGroups, addUserGroup, removeUserGroup, updateUserGroup, toggleItemInGroup,
    isCustomizing, setIsCustomizing, 
    isFocusMode, setIsFocusMode,
    isCommandPaletteOpen, setIsCommandPaletteOpen,
    usageStats, incrementUsage, resetOrder 
  } = useMenuStore()

  const toggleCustomizing = () => {
    const newVal = !isCustomizing
    setIsCustomizing(newVal)
    if (newVal) {
      toast(isRTL ? 'وضع التخصيص مفعل: اسحب العناصر لترتيبها، واستخدم ⚡ للترتيب الذكي' : 'Customize mode active: drag items to reorder, use ⚡ for smart sort', {
        icon: '🔧',
        duration: 4000,
        style: {
          borderRadius: '12px',
          background: '#1e293b',
          color: '#fff',
          border: '1px solid rgba(147, 51, 234, 0.3)',
          fontSize: '14px',
          padding: '12px 16px',
        },
      })
    } else {
      toast.success(isRTL ? 'تم حفظ الترتيب الجديد' : 'New order saved')
    }
  }

  // Support touch sensors for mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Store hasPermission in ref to avoid dependency changes
  const hasPermissionRef = useRef(hasPermission)
  useEffect(() => {
    hasPermissionRef.current = hasPermission
  }, [hasPermission])

  // Memoize user role to avoid recalculation
  const userRole = useMemo(() => {
    return user?.role || (user?.roles && user.roles.length > 0 ? user.roles[0] : null)
  }, [user?.role, user?.roles])

  const isAdmin = useMemo(() => {
    return userRole === 'Admin' || (user?.roles && user.roles.includes('Admin'))
  }, [userRole, user?.roles])

  // Load dynamic forms for menu
  const { data: menuForms } = useQuery({
    queryKey: ['customFormsMenu'],
    queryFn: () => customFormService.getMenuForms(),
    enabled: canAccess,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const { data: dynamicReportMenuData } = useQuery({
    queryKey: ['dynamic-reports-menu'],
    queryFn: dynamicReportService.menu,
    enabled: canAccess,
    staleTime: 60 * 1000,
  })
  const dynamicReportMenu = dynamicReportMenuData ?? EMPTY_DYNAMIC_REPORTS

  // Icon mapping - constant object
  const iconMap: Record<string, any> = {
    FiFileText,
    FiEdit,
    FiLayout,
    FiClipboard,
    FiFile,
    FiCheckSquare,
    FiList,
  }

  // Merge static menu items with dynamic forms
  const allMenuItems = useMemo(() => {
    // Deep clone menuItems to avoid mutating the original
    const items: MenuItem[] = menuItems.map(item => ({
      ...item,
      children: item.children
        ? item.children.map(child => ({
            ...child,
            children: child.children ? [...child.children] : undefined,
          }))
        : undefined
    }))
    
    // Find "النماذج الديناميكية" group
    let formsGroupIndex = items.findIndex(
      item => item.label.ar === 'النماذج الديناميكية' || item.label.en === 'Dynamic Forms'
    )
    
    // If group doesn't exist and we have forms, create it
    if (formsGroupIndex === -1 && Array.isArray(menuForms) && menuForms.length > 0) {
      items.push({
        label: { ar: 'النماذج الديناميكية', en: 'Dynamic Forms', fr: 'Formulaires Dynamiques' },
        icon: FiLayout,
        children: []
      })
      formsGroupIndex = items.length - 1
    }
    
    // Add dynamic forms to the group
    if (formsGroupIndex !== -1 && Array.isArray(menuForms) && menuForms.length > 0) {
      const dynamicFormItems: MenuItem[] = [...menuForms]
        .sort((a, b) => (a.menuOrder || 999) - (b.menuOrder || 999))
        .map(form => ({
          path: `/custom-forms/${form.id}/view`,
          label: {
            ar: form.name,
            en: form.name,
            fr: form.name
          },
          icon: iconMap[form.menuIcon || 'FiFileText'] || FiFileText
        }))
      
      // Get existing children (excluding dynamic forms to avoid duplicates)
      const existingChildren = items[formsGroupIndex].children || []
      const staticChildren = existingChildren.filter(
        child => !child.path?.startsWith('/custom-forms/') || child.path === '/custom-forms'
      )
      
      // Merge static children with new dynamic forms
      items[formsGroupIndex] = {
        ...items[formsGroupIndex],
        children: [...staticChildren, ...dynamicFormItems]
      }
    }
    
    const screensGroupChildren: MenuItem[] = studioScreens.map((screen) => ({
      path: `/settings/studio?tab=designer&screenId=${screen.id}`,
      label: {
        ar: screen.name || 'شاشة جديدة',
        en: screen.nameEn || screen.name || 'New Screen',
        fr: screen.nameEn || screen.name || 'Nouvel écran',
      },
      icon: FiLayout,
    }))

    if (screensGroupChildren.length > 0) {
      const screensGroup: MenuItem = {
        label: { ar: 'الشاشات الجديدة', en: 'New Screens', fr: 'Nouveaux écrans' },
        icon: FiFolder,
        children: screensGroupChildren,
      }

      const studioIndex = items.findIndex(
        item => item.label.ar === 'ستديو التطوير' || item.label.en === 'Development Studio'
      )

      if (studioIndex !== -1) {
        items[studioIndex] = {
          ...items[studioIndex],
          children: [...(items[studioIndex].children || []), screensGroup],
        }
      } else {
        items.unshift(screensGroup)
      }
    }

    const reportsIndex = items.findIndex(
      item => item.label.ar === 'التقارير' || item.label.en === 'Reports'
    )
    if (reportsIndex !== -1 && dynamicReportMenu.length > 0) {
      const reportsGroup = items[reportsIndex]
      const children = [...(reportsGroup.children || [])]
      let dynamicIndex = children.findIndex(
        child => child.label.ar === 'التقارير الديناميكية' || child.label.en === 'Dynamic Reports'
      )
      if (dynamicIndex === -1) {
        children.push({
          label: { ar: 'التقارير الديناميكية', en: 'Dynamic Reports', fr: 'Rapports dynamiques' },
          icon: FiBarChart,
          children: [],
        })
        dynamicIndex = children.length - 1
      }
      const dynamicGroup = children[dynamicIndex]
      const staticChildren = (dynamicGroup.children || []).filter(
        child => !child.path?.match(/^\/dynamic-reports\/\d+$/)
      )
      const generated = dynamicReportMenu.map((report) => ({
        path: report.path,
        label: { ar: report.menuName || report.name, en: report.menuName || report.name, fr: report.menuName || report.name },
        icon: FiBarChart,
      }))
      children[dynamicIndex] = {
        ...dynamicGroup,
        children: [...staticChildren, ...generated],
      }
      items[reportsIndex] = { ...reportsGroup, children }
    }

    return items
  }, [menuForms, studioScreens, dynamicReportMenu])

  const toggleGroup = (groupLabel: string) => {
    const newOpenGroups = new Set(openGroups)
    if (newOpenGroups.has(groupLabel)) {
      newOpenGroups.delete(groupLabel)
    } else {
      newOpenGroups.add(groupLabel)
    }
    setOpenGroups(newOpenGroups)
  }

  const isGroupOpen = (groupLabel: string) => {
    return openGroups.has(groupLabel)
  }

  const isActive = (item: MenuItem) => {
    if (item.path) {
      return currentPath === item.path || currentPath.startsWith(item.path + '/')
    }
    // Check if any child is active
    if (item.children) {
      return item.children.some(child => isActive(child))
    }
    return false
  }

  // Memoize hasAccess function to prevent infinite loops
  const checkItemAccess = useCallback((item: MenuItem): boolean => {
    // Admin normally has all permissions, but SignalSystemManagement/MenuVisibility overrides this
    // So we MUST check hasPermission even for admins
    if (item.path) {
      return hasPermission(item.path)
    }
    
    if (item.children) {
      // For groups, show if at least one child is allowed
      return item.children.some(child => checkItemAccess(child))
    }
    
    return false
  }, [hasPermission])

  // Filter menu items based on search term
  const matchesSearch = (item: MenuItem, term: string): boolean => {
    if (!term) return true
    
    // Safety check: Don't match if user doesn't have permission to this item or its children
    if (!checkItemAccess(item)) {
      return false
    }
    
    const searchLower = term.toLowerCase()
    const labelAr = item.label.ar?.toLowerCase() || ''
    const labelEn = item.label.en?.toLowerCase() || ''
    const labelFr = item.label.fr?.toLowerCase() || ''
    
    // Check if label matches
    if (labelAr.includes(searchLower) || labelEn.includes(searchLower) || labelFr.includes(searchLower)) {
      return true
    }
    
    // Check children
    if (item.children) {
      return item.children.some(child => matchesSearch(child, term))
    }
    
    return false
  }

  // Get module settings
  const modules = useModuleStore()

  // Filter menu items based on search and license status
  const filteredMenuItems = useMemo(() => {
    let items: any = allMenuItems

    // 1. Filter by modules first
    items = filterMenuItems(items, modules) as any

    // 2. Filter by access
    if (!canAccess) {
      items = items.filter(item => item.path === '/settings/license' && checkItemAccess(item))
    } else {
      items = items.filter(item => checkItemAccess(item))
    }

    // 3. Filter by search
    if (searchTerm) {
      items = items.filter(item => matchesSearch(item, searchTerm))
    } else {
      // 4. Filter by visibility (unless in customizing mode)
      if (!isCustomizing) {
        items = items.filter(item => !hiddenItems.includes(item.path || item.label.ar))
      }
    }

    // 5. Focus Mode filtering
    if (isFocusMode && !isCustomizing && !searchTerm) {
      items = items.filter(item => {
        const itemId = item.path || item.label.ar
        return pinnedItems.includes(item.path || '') || recentItems.includes(item.path || '')
      })
    }

    // 6. Apply custom order
    if (sidebarOrder.length > 0 && !searchTerm) {
      items = [...items].sort((a, b) => {
        const aId = a.path || a.label.ar
        const bId = b.path || b.label.ar
        const aIdx = sidebarOrder.indexOf(aId)
        const bIdx = sidebarOrder.indexOf(bId)
        
        if (aIdx === -1 && bIdx === -1) return 0
        if (aIdx === -1) return 1
        if (bIdx === -1) return -1
        return aIdx - bIdx
      })
    }

    return items
  }, [allMenuItems, modules, searchTerm, canAccess, checkItemAccess, sidebarOrder, isCustomizing, hiddenItems, isFocusMode, pinnedItems, recentItems])

  // Handle Drag End
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      // Check if it's a pinned item
      if (pinnedItems.includes(active.id as string)) {
        const oldIndex = pinnedItems.indexOf(active.id as string)
        const newIndex = pinnedItems.indexOf(over.id as string)
        if (newIndex !== -1) {
          const newPinned = arrayMove(pinnedItems, oldIndex, newIndex)
          setPinnedItems(newPinned)
        }
        return
      }

      // Handle sidebar order
      const oldIndex = filteredMenuItems.findIndex((i: any) => (i.path || i.label.ar) === active.id)
      const newIndex = filteredMenuItems.findIndex((i: any) => (i.path || i.label.ar) === over.id)
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(filteredMenuItems, oldIndex, newIndex)
        setSidebarOrder(newItems.map((i: any) => i.path || i.label.ar))
      }
    }
  }

  // Get pinned items data
  const pinnedMenuItems = useMemo(() => {
    const findItemByPath = (items: MenuItem[], path: string): MenuItem | null => {
      for (const item of items) {
        if (item.path === path) return item
        if (item.children) {
          const found = findItemByPath(item.children, path)
          if (found) return found
        }
      }
      return null
    }
    
    return pinnedItems
      .map(path => findItemByPath(allMenuItems, path))
      .filter((item): item is MenuItem => item !== null)
  }, [pinnedItems, allMenuItems])

  // Smart Sort: Sort by usage
  const smartSort = () => {
    const sorted = [...filteredMenuItems].sort((a: any, b: any) => {
      const aUsage = usageStats[a.path || ''] || 0
      const bUsage = usageStats[b.path || ''] || 0
      return bUsage - aUsage
    })
    setSidebarOrder(sorted.map((i: any) => i.path || i.label.ar))
  }

  // Auto-open groups that contain search results
  useEffect(() => {
    if (searchTerm) {
      const groupsToOpen = new Set<string>()
      // Use allMenuItems instead of filteredMenuItems to avoid dependency loop
      allMenuItems.forEach(item => {
        if (item.children && item.children.length > 0) {
          // Check if any child matches search
          const hasMatchingChild = item.children.some(child => {
            const childLabel = child.label[language] || child.label.en || child.label.ar
            const searchLower = searchTerm.toLowerCase()
            return childLabel.toLowerCase().includes(searchLower)
          })
          if (hasMatchingChild) {
            const label = item.label[language] || item.label.en || item.label.ar
            groupsToOpen.add(label)
          }
        }
      })
      setOpenGroups(groupsToOpen)
    } else {
      setOpenGroups((prev) => (prev.size === 0 ? prev : new Set()))
    }
  }, [searchTerm, allMenuItems, language])

  // Reset search when closing sidebar to avoid قائمة فارغة بالخطأ
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.path) {
      // Final security check before navigation
      if (!hasPermission(item.path)) {
        toast.error(isRTL ? 'ليس لديك صلاحيات للدخول لهذه الصفحة' : 'You do not have permission to access this page')
        return
      }

      const title = item.label[language] || item.label.en || item.label.ar
      const opened = addTab({
        id: item.path.replace(/\//g, '-').substring(1) || 'dashboard',
        title: title,
        basePath: item.path,
        lastPath: item.path
      })
      if (opened) navigate(item.path)
    }
  }

  const renderMenuItem = (item: MenuItem, level: number = 0, index: number = -1) => {
    // إذا لم يكن الترخيص موافق عليه، أظهر فقط صفحة التراخيص
    if (!canAccess && item.path !== '/settings/license') {
      return null
    }
    
    // Check if user has access to this item
    if (!checkItemAccess(item)) {
      return null
    }

    const itemId = item.path || item.label.ar
    const label = itemLabels[itemId] || item.label[language] || item.label.en || item.label.ar
    const hasChildren = item.children && item.children.length > 0
    const isItemActive = isActive(item)
    const isGroupOpenNow = hasChildren ? isGroupOpen(label) : false
    const isHidden = hiddenItems.includes(itemId)
    const customColor = itemColors[itemId]
    
    // Icon Logic
    const CustomIcon = itemIcons[itemId] ? (iconOptions[itemIcons[itemId] as keyof typeof iconOptions] || item.icon) : item.icon

    // Hide if marked as hidden (unless customizing or searching)
    if (isHidden && !isCustomizing && !searchTerm) {
      return null
    }

    const content = hasChildren ? (
      <li key={`${label}-${item.path || (item.children ? item.children.map(c => c.path || c.label?.ar || c.label?.en || '').join('|') : '')}`}>
        <button
          onClick={() => isExpanded && toggleGroup(label)}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isRTL ? 'flex-row-reverse' : 'flex-row'
          } ${isCustomizing ? (isRTL ? 'pr-8' : 'pl-8') : ''} ${!isExpanded ? 'justify-center px-2' : ''}`}
          style={{
            background: customColor?.backgroundColor || (isItemActive
              ? 'linear-gradient(90deg, rgba(147, 51, 234, 0.2) 0%, rgba(147, 51, 234, 0.1) 100%)'
              : (level > 0 ? 'rgba(255, 255, 255, 0.035)' : 'transparent')),
            color: customColor?.textColor || (isItemActive ? '#c4b5fd' : (level > 0 ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.7)')),
            borderRight: isItemActive && isRTL ? '4px solid #a78bfa' : (level > 0 && isRTL ? '2px solid rgba(255, 255, 255, 0.04)' : 'none'),
            borderLeft: isItemActive && !isRTL ? '4px solid #a78bfa' : (level > 0 && !isRTL ? '2px solid rgba(255, 255, 255, 0.04)' : 'none'),
          }}
          title={!isExpanded ? label : ''}
        >
          {CustomIcon && (
            <CustomIcon className={`text-xl flex-shrink-0 ${isRTL ? 'order-3' : 'order-1'}`} />
          )}
          {isExpanded && (
            <>
              <span className={`flex-1 ${isRTL ? 'text-right order-2' : 'text-left order-2'}`}>{label}</span>
              <span className={`text-sm flex-shrink-0 ${isRTL ? 'order-1' : 'order-3'}`}>
                {isGroupOpenNow ? <FiChevronDown /> : <FiChevronRight />}
              </span>
            </>
          )}
        </button>
        {isGroupOpenNow && isExpanded && (
          <ul className={`mt-1 space-y-1 ${isRTL ? 'pr-4' : 'pl-4'}`}>
            {item.children!.map((child, idx) => renderMenuItem(child, level + 1, idx)).filter(Boolean)}
          </ul>
        )}
      </li>
    ) : (
      <li key={`${item.path || label}-${label}`}>
        <div className="relative group/item">
          <Link
            to={item.path || '#'}
            onClick={(e) => {
              if (item.path) {
                e.preventDefault()
                handleMenuItemClick(item)
                incrementUsage(item.path)
                addRecentItem(item.path)
              }
            }}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isRTL ? 'flex-row-reverse' : 'flex-row'
            } ${level > 0 ? (isRTL ? 'pr-8' : 'pl-8') : ''} ${isCustomizing && level === 0 ? (isRTL ? 'pr-8' : 'pl-8') : ''} ${!isExpanded ? 'justify-center px-2' : ''}`}
            style={{
              background: customColor?.backgroundColor || (isItemActive
                ? 'linear-gradient(90deg, rgba(147, 51, 234, 0.2) 0%, rgba(147, 51, 234, 0.1) 100%)'
                : (level > 0 ? 'rgba(255, 255, 255, 0.035)' : 'transparent')),
              color: customColor?.textColor || (isItemActive ? '#c4b5fd' : (level > 0 ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.7)')),
              borderRight: isItemActive && isRTL ? '4px solid #a78bfa' : (level > 0 && isRTL ? '2px solid rgba(255, 255, 255, 0.04)' : 'none'),
              borderLeft: isItemActive && !isRTL ? '4px solid #a78bfa' : (level > 0 && !isRTL ? '2px solid rgba(255, 255, 255, 0.04)' : 'none'),
            }}
            title={!isExpanded ? label : ''}
          >
            {isExpanded && (
              <span className={`flex-1 ${isRTL ? 'text-right order-1' : 'text-left order-2'} ${level > 0 ? 'text-sm' : ''}`}>{label}</span>
            )}
            {CustomIcon && (
              <CustomIcon className={`text-xl flex-shrink-0 ${isRTL ? 'order-2' : 'order-1'}`} />
            )}
          </Link>

          {/* Quick Add Button (+) */}
          {item.path && !isCustomizing && isExpanded && (item.path === '/invoices' || item.path === '/items' || item.path === '/customers' || item.path === '/vendors' || item.path === '/stores') && (
            <Link
              to={`${item.path}/new`}
              className={`absolute p-1 rounded-full bg-primary-500 text-white shadow-lg opacity-0 group-hover/item:opacity-100 transition-opacity active:scale-90 z-20`}
              style={{
                top: 10,
                [isRTL ? 'left' : 'right']: 36 as any,
              }}
              title={isRTL ? 'إضافة جديد' : 'Add New'}
            >
              <FiPlus size={12} />
            </Link>
          )}

          {item.path && !isCustomizing && isExpanded && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                togglePinnedItem(item.path!)
              }}
              title={isRTL ? 'تثبيت في الوصول السريع' : 'Pin to Quick Access'}
              className="absolute p-1 rounded hover:opacity-100 opacity-60 transition-all active:scale-90"
              style={{
                top: 8,
                [isRTL ? 'left' : 'right']: 6 as any,
                background: 'transparent',
                color: pinnedItems.includes(item.path!) ? '#facc15' : 'var(--sidebar-text)'
              }}
            >
              <FiStar size={14} fill={pinnedItems.includes(item.path!) ? '#facc15' : 'none'} />
            </button>
          )}

          {item.path && !isCustomizing && isExpanded && isStudioScreenPath(item.path) && (
            <div
              className="absolute flex items-center gap-1"
              style={{
                top: 8,
                [isRTL ? 'left' : 'right']: 28 as any,
              }}
            >
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  renameStudioScreenFromMenu(item.path)
                }}
                className="p-1 rounded hover:bg-white/10 text-blue-300"
                title={isRTL ? 'إعادة تسمية الشاشة' : 'Rename screen'}
              >
                <FiEdit size={13} />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  deleteStudioScreenFromMenu(item.path)
                }}
                className="p-1 rounded hover:bg-red-500/20 text-red-400"
                title={isRTL ? 'حذف الشاشة' : 'Delete screen'}
              >
                <FiTrash2 size={13} />
              </button>
            </div>
          )}
        </div>
      </li>
    )

    if (level === 0 && !searchTerm) {
      return (
        <SortableItem 
          key={itemId} 
          id={itemId} 
          isCustomizing={isCustomizing} 
          isRTL={isRTL}
          isHidden={isHidden}
          onToggleVisibility={() => toggleHiddenItem(itemId)}
          customColors={customColor}
          onSetColors={(colors) => setItemColors(itemId, colors)}
          onResetColors={() => resetItemColors(itemId)}
          isFirst={index === 0}
          onSetIcon={(iconName) => setItemIcon(itemId, iconName)}
          onSetLabel={(label) => setItemLabel(itemId, label)}
          onTogglePinned={item.path ? () => togglePinnedItem(item.path!) : undefined}
          isPinned={item.path ? pinnedItems.includes(item.path!) : false}
          currentLabel={label}
          userGroups={userGroups}
          onToggleInGroup={(groupId) => toggleItemInGroup(groupId, itemId)}
        >
          {content}
        </SortableItem>
      )
    }

    return content
  }

  return (
    <>
      <aside
        ref={sidebarRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        key={`sidebar-aside-${direction}-${language}`}
        className={`sidebar-container flex flex-col transition-all duration-500 relative z-50 h-full shadow-2xl border-r border-white/10 ${
          isExpanded ? 'w-72' : 'w-20'
        }`}
        style={{ 
          background: 'linear-gradient(180deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.98) 100%)',
          backdropFilter: 'blur(12px)',
          color: 'var(--sidebar-text)',
          direction: direction,
          height: '100vh'
        }}
        dir={direction}
      >
        <div 
          className={`p-6 border-b flex items-center justify-between ${isRTL ? 'flex-row-reverse' : 'flex-row'} ${!isExpanded ? 'justify-center px-2' : ''}`}
          style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}
        >
          {isExpanded ? (
            <>
              <div className="flex flex-col">
                <h1 className="text-2xl font-black bg-gradient-to-r from-primary-300 to-primary-500 bg-clip-text text-transparent tracking-tighter">
                  SmartAccount
                </h1>
                <span className="text-[10px] uppercase tracking-[0.2em] text-primary-500/60 font-bold">Enterprise Edition</span>
              </div>
              <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                <button
                  onClick={toggleCustomizing}
                  className={`p-2 rounded-xl transition-all duration-300 ${isCustomizing ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/50' : 'hover:bg-white/10'}`}
                  style={{ color: isCustomizing ? 'white' : 'var(--sidebar-text)' }}
                  title={isRTL ? 'تخصيص القائمة' : 'Customize Menu'}
                >
                  <FiSettings className={`text-lg ${isCustomizing ? 'animate-spin-slow' : ''}`} />
                </button>
                <button
                  onClick={toggleLanguage}
                  className="p-2 hover:bg-white/10 rounded-xl transition-all duration-300 hover:rotate-12 active:scale-90"
                  style={{ color: 'var(--sidebar-text)' }}
                  title={isRTL ? 'Switch to English' : 'التبديل إلى العربية'}
                >
                  <FiGlobe className="text-lg" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleContentFullscreen()
                  }}
                  className="p-2 rounded-xl transition-all duration-300 active:scale-90 bg-primary-500/30 text-primary-300 hover:bg-primary-500 hover:text-white border border-primary-400/40"
                  title={isRTL ? 'ملء الشاشة' : 'Fullscreen'}
                >
                  <FiMaximize2 className="text-lg" />
                </button>
                <button
                  onClick={onToggle}
                  className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-xl transition-all duration-300 active:scale-90"
                  style={{ color: 'var(--sidebar-text)' }}
                  title={isRTL ? 'إغلاق القائمة' : 'Close Menu'}
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={onToggle}
              className="p-2 rounded-xl bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-all"
            >
              <FiMenu className="text-xl" />
            </button>
          )}
        </div>

        {/* Customization Toolbar */}
        {isCustomizing && isExpanded && (
          <div className="px-6 py-3 bg-primary-500/10 border-b border-primary-500/20 flex items-center justify-between animate-fade-in">
            <span className="text-[10px] font-bold text-primary-400 uppercase tracking-wider">
              {isRTL ? 'وضع التخصيص' : 'Custom Mode'}
            </span>
            <div className="flex gap-2">
              <button 
                onClick={() => setIsFocusMode(!isFocusMode)}
                className={`p-1.5 rounded-lg transition-colors ${isFocusMode ? 'bg-primary-500 text-white' : 'hover:bg-white/10 text-primary-400'}`}
                title={isRTL ? 'وضع التركيز' : 'Focus Mode'}
              >
                <FiTarget size={14} />
              </button>
              <button 
                onClick={handleCreateGroup}
                className="p-1.5 hover:bg-white/10 rounded-lg text-primary-400 transition-colors"
                title={isRTL ? 'إنشاء مجموعة مخصصة' : 'Create Custom Group'}
              >
                <FiFolderPlus size={14} />
              </button>
              <button 
                onClick={smartSort}
                className="p-1.5 hover:bg-white/10 rounded-lg text-primary-400 transition-colors"
                title={isRTL ? 'ترتيب ذكي (الأكثر استخداماً)' : 'Smart Sort (Most Used)'}
              >
                <FiZap size={14} />
              </button>
              <button 
                onClick={resetOrder}
                className="p-1.5 hover:bg-red-500/10 rounded-lg text-red-400 transition-colors"
                title={isRTL ? 'إعادة تعيين' : 'Reset Order'}
              >
                <FiRefreshCw size={14} />
              </button>
            </div>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className={`p-6 ${!isExpanded ? 'p-4' : ''}`}>
            <div className="relative group command-palette-trigger">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none transition-colors group-focus-within:text-primary-400 ${!isExpanded ? 'relative p-0 justify-center w-full' : ''}`}>
                <FiSearch className={`text-lg opacity-50 group-focus-within:opacity-100 ${!isExpanded ? 'opacity-100 text-primary-400' : ''}`} />
              </div>
              {isExpanded && (
                <>
                  <input
                    type="text"
                    value={searchTerm}
                    readOnly
                    onClick={() => setIsCommandPaletteOpen(true)}
                    placeholder={isRTL ? 'ابحث عن ميزة...' : 'Search features...'}
                    autoComplete="off"
                    spellCheck={false}
                    className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:bg-white/10 transition-all duration-300 placeholder:text-white/20`}
                    style={{ color: 'white' }}
                  />
                  {!searchTerm && (
                    <div className={`absolute inset-y-0 ${isRTL ? 'left-4' : 'right-4'} flex items-center pointer-events-none`}>
                      <span className="text-[10px] text-white/20 border border-white/10 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <FiCommand size={10} /> K
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            {isFocusMode && !isCustomizing && isExpanded && (
              <div className="mt-4 px-4 py-2 bg-primary-500/10 border border-primary-500/20 rounded-xl flex items-center justify-between animate-pulse">
                <div className="flex items-center gap-2">
                  <FiTarget className="text-primary-400" size={14} />
                  <span className="text-[11px] font-bold text-primary-400 uppercase tracking-widest">
                    {isRTL ? 'وضع التركيز نشط' : 'Focus Mode Active'}
                  </span>
                </div>
                <button 
                  onClick={() => setIsFocusMode(false)}
                  className="text-[10px] text-white/40 hover:text-white transition-colors underline"
                >
                  {isRTL ? 'إيقاف' : 'Disable'}
                </button>
              </div>
            )}
          </div>
          
          <nav className="flex-1 overflow-y-auto px-4 pb-8 sidebar-scrollbar">
            {isPermissionsLoading ? (
              <div className="flex flex-col items-center justify-center py-20 opacity-50">
                <LoadingSpinner size="sm" />
                {isExpanded && (
                  <p className="text-[10px] mt-4 text-white/40 uppercase tracking-widest animate-pulse">
                    {isRTL ? 'جاري التحقق من الصلاحيات...' : 'Verifying Permissions...'}
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* User Custom Groups */}
                {userGroups.length > 0 && !searchTerm && isExpanded && (
                  <div className="mb-6 space-y-1.5 animate-fade-in">
                    <span className="text-[10px] font-bold text-white/20 uppercase tracking-[0.2em] px-4 mb-2 block">
                      {isRTL ? 'مجموعاتي الخاصة' : 'My Custom Groups'}
                    </span>
                    {userGroups.map(group => (
                      <div key={group.id} className="relative group/usergroup">
                        <button
                          onClick={() => toggleGroup(group.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:bg-white/5 border border-transparent hover:border-white/5 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
                        >
                          <FiFolder className="text-primary-400" size={20} />
                          <span className={`flex-1 font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{group.label}</span>
                          <span className="text-white/20">{openGroups.has(group.id) ? <FiChevronDown /> : <FiChevronRight />}</span>
                        </button>
                        
                        {isCustomizing && (
                          <button
                            onClick={() => removeUserGroup(group.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/usergroup:opacity-100 transition-opacity z-10"
                          >
                            <FiX size={10} />
                          </button>
                        )}

                        {openGroups.has(group.id) && (
                          <ul className={`mt-1 space-y-1 ${isRTL ? 'pr-6' : 'pl-6'}`}>
                            {group.items.length > 0 ? (
                              group.items.map(path => {
                                const findItem = (items: MenuItem[]): MenuItem | null => {
                                  for (const i of items) {
                                    if (i.path === path) return i
                                    if (i.children) {
                                      const found = findItem(i.children)
                                      if (found) return found
                                    }
                                  }
                                  return null
                                }
                                const item = findItem(allMenuItems)
                                if (!item) return null
                                return renderMenuItem(item, 1)
                              })
                            ) : (
                              <li className="py-2 px-4 text-[11px] text-white/20 italic">
                                {isRTL ? 'اسحب العناصر هنا (قريباً)' : 'Empty group'}
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    ))}
                    <div className="h-px w-full bg-white/5 my-4" />
                  </div>
                )}

                {filteredMenuItems.length === 0 && searchTerm ? (
                  <div className="text-center py-12 animate-pulse">
                    <div className="inline-block p-4 bg-white/5 rounded-full mb-4">
                      <FiSearch className="text-3xl text-white/20" />
                    </div>
                    <p className="text-sm font-medium text-white/40">
                      {isRTL ? 'لم نجد ما تبحث عنه' : 'No results found'}
                    </p>
                  </div>
                ) : (
                  <SortableContext
                    items={filteredMenuItems.map(i => i.path || i.label.ar)}
                    strategy={verticalListSortingStrategy}
                  >
                    <ul className={`space-y-1.5 ${!isExpanded ? 'flex flex-col items-center' : ''}`}>
                      {filteredMenuItems.map((item, idx) => renderMenuItem(item, 0, idx)).filter(Boolean)}
                    </ul>
                  </SortableContext>
                )}
              </>
            )}
          </nav>
        </DndContext>

        <div className={`p-4 m-4 rounded-2xl bg-gradient-to-br from-primary-500/10 to-primary-700/10 border border-white/5 backdrop-blur-sm ${!isExpanded ? 'm-2 p-2 rounded-xl flex justify-center' : ''}`}>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'} ${!isExpanded ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center font-bold text-white shadow-lg flex-shrink-0">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            {isExpanded && (
              <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                <p className="text-sm font-bold text-white truncate">{user?.username || 'User'}</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] text-white/40 truncate uppercase tracking-wider">{user?.role || 'Member'}</p>
                  <p className="text-[9px] text-primary-400/50 font-mono">v{getAppVersion()}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
